<template>
  <div id="google-maps-wrapper" :style="{'height': height}">
    <div class="google-maps"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as GoogleMapsLoader from 'google-maps'
import * as MarkerClusterer from '@google/markerclusterer'
import circleConfig from './circle-config'

export default {
  name: 'google-map',
  props: {
    fitBounds: {
      type: Boolean,
      required: false,
      default: true
    },
    height: {
      type: String,
      required: false,
      default: '60vh'
    },
    locations: {
      type: Array,
      required: false,
      default: () => []
    },
    center: {
      type: Object,
      required: false,
      default: () => { return { lat: 39.7432886, lng: -8.8197383 } }
    },
    shapes: {
      type: Array,
      required: false,
      default: () => []
    },
    zoom: {
      type: Number,
      required: false,
      default: 7
    },
    markerIcon: {
      type: String,
      required: false,
      default: require('../../../assets/icons/map_marker.svg')
    },
    clusterIcon: {
      type: String,
      required: false,
      default: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
    }
  },
  mounted () {
    GoogleMapsLoader.KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY
    GoogleMapsLoader.LIBRARIES.push('places')
    GoogleMapsLoader.load(this.load)
    GoogleMapsLoader.onLoad(() => this.$emit('googleMapsLoaded', true))
  },
  computed: mapGetters({
    config: 'config'
  }),
  data: function () {
    return {
      markerUrl: undefined,
      map: undefined,
      markers: [],
      markerClusterer: undefined,
      circles: []
    }
  },
  methods: {
    isLoaded: function () {
      return GoogleMapsLoader.isLoaded()
    },
    clearMarkers: function () {
      this.markerClusterer.clearMarkers()
      this.markers = []
    },
    addMarkers: function (locations) {
      let bounds = new google.maps.LatLngBounds() // eslint-disable-line
      this.markers = locations.map(location => this.newMarker(location))
      this.markerClusterer.addMarkers(this.markers)
      // if (this.fitBounds) {
      //   this.fitMapBounds()
      // }
    },
    fitMapBounds: function () {
      let bounds = new google.maps.LatLngBounds() // eslint-disable-line
      this.markers.forEach(marker => {
        bounds.extend(marker.position)
      })
      this.map.fitBounds(bounds)
    },
    newMarker: function (location) {
      let scaledSize = new google.maps.Size(35, 35) // eslint-disable-line
      let size = new google.maps.Size(35, 35) // eslint-disable-line

      let icon = {
        size,
        scaledSize,
        url: location.icon ? location.icon : this.markerIcon
      }
      let marker = new google.maps.Marker({ // eslint-disable-line
        position: location.position,
        id: location.id,
        icon,
      })
      if (location.label) {
        marker.label = location.label
      }

      let infowindow = null
      if (location.window) {
        infowindow = new google.maps.InfoWindow({ // eslint-disable-line
          content: location.window.content
        })
        if (location.window.open) infowindow.open(this.map, marker)
      }

      marker.addListener('click', () => {
        if (location.window) infowindow.open(this.map, marker)
        this.$emit('onClickMarker', marker)
      })
      return marker
    },
    updateMarkers: function () {
      if (this.map && this.markerClusterer) {
        this.clearMarkers()
        this.addMarkers(this.locations)
      }
    },
    clearCircles: function () {
      this.circles.forEach(circle => {
        circle.setMap(null)
        circle.setVisible(false)
        circle = null
      })
    },
    load: function (google) {
      /* eslint-disable no-new */
      this.map = new google.maps.Map(this.$el, {
        center: new google.maps.LatLng(this.center.lat, this.center.lng),
        zoom: this.zoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP
      })
      this.map.addListener('click', this.onClickMap)
      this.markerClusterer = new MarkerClusterer(this.map, [], { imagePath: this.clusterIcon })
      this.updateMarkers()
    },
    updateCenter: function (newValue) {
      this.map.panTo(newValue)
    },
    updateZoom: function (newValue) {
      this.map.setZoom(newValue)
    },
    onClickMap: function (event) {
      this.$emit('map-clicked', { lat: event.latLng.lat(), lng: event.latLng.lng() })
    },
    setCursor: function (draggableCursor) {
      this.map.setOptions({ draggableCursor })
    },
    addShapes: function (shapes) {
      shapes.forEach(shape => {
        let circle = new google.maps.Circle({ //eslint-disable-line
          ...circleConfig,
          ...shape,
          map: this.map,
          draggable: true
        })
        circle.addListener('radius_changed', () => {
          this.$emit('circle-radius-changed', {
            radius: (circle.radius / 1000).toFixed(),
            circle
          })
        })
        // this.map.event.addListener(circle, 'radius_changed', (payload) => )
        this.circles.push(circle)
      })
    }
  },
  watch: {
    locations: 'updateMarkers',
    center: 'updateCenter',
    zoom: 'updatedZoom',
    fitBounds: 'updateMarkers',
    shapes: {
      handler: function (shapes) {
        this.clearCircles()
        this.addShapes(shapes)
      }
    }
  }
}
</script>

<style lang="scss">
  .google-maps {
    height: 100%;
  }
</style>
